import { DateTime } from "luxon";

type SortableFleetEvent = {
  eventType: "booking" | "blocking";
  start: string;
  end: string;
};

/** Normalize start/end to ISO string for API and local state (values may be Date from MRT). */
export function toIsoString(value: string | Date | null | undefined): string | null {
  if (value == null) return null;
  if (typeof value === "string") return value || null;
  if (value instanceof Date) return Number.isFinite(value.getTime()) ? value.toISOString() : null;
  return null;
}

export const compareIsoAsc = (a: string, b: string): number =>
  DateTime.fromISO(a).toMillis() - DateTime.fromISO(b).toMillis();

/**
 * Sort rows by fleet event priority:
 * 1) today's booking start, 2) today's booking end, 3) non-past events closest to now,
 * 4) today's blocking, 5) past events in reverse date order (regardless of type).
 */
export function sortFleetEvents<T extends SortableFleetEvent>(rows: T[], timezone: string): T[] {
  const now = DateTime.now().setZone(timezone);
  const todayStart = now.startOf("day");
  const tomorrowStart = todayStart.plus({ days: 1 });

  const isToday = (iso: string): boolean => {
    const dt = DateTime.fromISO(iso).setZone(timezone);
    return dt >= todayStart && dt < tomorrowStart;
  };

  const isPast = (iso: string): boolean => DateTime.fromISO(iso).setZone(timezone) < now;
  const toMillis = (iso: string): number => DateTime.fromISO(iso).setZone(timezone).toMillis();

  const bucket = (row: T): number => {
    if (row.eventType === "booking" && isToday(row.start) && !isPast(row.end)) return 0;
    if (row.eventType === "booking" && isToday(row.end) && !isPast(row.end)) return 1;
    if (row.eventType === "blocking" && isToday(row.start) && !isPast(row.end)) return 3;
    if (!isPast(row.end)) return 2;
    return 4;
  };

  return [...rows].sort((a, b) => {
    const aBucket = bucket(a);
    const bBucket = bucket(b);
    if (aBucket !== bBucket) return aBucket - bBucket;

    if (aBucket === 2 && bBucket === 2) {
      const aDistanceToNow = Math.abs(toMillis(a.start) - now.toMillis());
      const bDistanceToNow = Math.abs(toMillis(b.start) - now.toMillis());
      if (aDistanceToNow !== bDistanceToNow) return aDistanceToNow - bDistanceToNow;
    }

    if (aBucket === 4 && bBucket === 4) {
      return compareIsoAsc(b.start, a.start);
    }

    return compareIsoAsc(a.start, b.start);
  });
}
